/**
 * Keeps the address bar in sync with the focused tab (deep links in, history entries out, back/forward) and reports
 * each navigation as an analytics screen view.
 */
import { Shell, type ShellState, type ShellViewModel } from "@bundle/core";
import { useEffect, useRef } from "react";
import { usePlatform } from "./PlatformContext.ts";

function current(): string {
  return `${location.pathname}${location.search}`;
}

export function useUrlSync(shell: ShellViewModel, state: ShellState, slug: string): void {
  const started = useRef(false);
  const lastTab = useRef<string | undefined>(undefined);

  // Deep link on first mount: open the URL in the focused tab when it belongs to this workspace.
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const route = Shell.parseUrl(current());
    if (route !== undefined && route.ws === slug && Shell.normalizeUrl(current()) !== Shell.activeTab(shell.state).url) {
      shell.open(current(), "current");
    }
  }, [shell, slug]);

  // Focused tab → address bar: a new tab replaces, navigation inside a tab pushes.
  const tab = Shell.activeTab(state);

  // Analytics screen_view per navigation: the route type only ("channel", "thread"), never the URL.
  const { analytics } = usePlatform();
  useEffect(() => {
    analytics.screen(Shell.parseUrl(tab.url)?.type ?? "unknown");
  }, [analytics, tab.url]);
  useEffect(() => {
    if (current() === tab.url) {
      lastTab.current = tab.id;
      return;
    }
    if (lastTab.current === tab.id) history.pushState({ tabId: tab.id }, "", tab.url);
    else history.replaceState({ tabId: tab.id }, "", tab.url);
    lastTab.current = tab.id;
  }, [tab.id, tab.url]);

  // Browser back/forward → the focused tab's history.
  useEffect(() => {
    const onPop = () => {
      const url = Shell.normalizeUrl(current());
      const t = Shell.activeTab(shell.state);
      if (t.back[t.back.length - 1] === url) shell.back();
      else if (t.forward[0] === url) shell.forward();
      else if (url !== t.url) shell.open(url, "current");
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [shell]);
}
